import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AppShellSkeleton from '@/components/dashboard/AppShellSkeleton';
import { useSupabaseAuth } from '@/hooks/auth/supabaseAuth';

interface AuthLoadingScreenProps {
  message?: string;
  showSkeleton?: boolean;
  timeoutSeconds?: number;
}

const AuthLoadingScreen: React.FC<AuthLoadingScreenProps> = ({
  message = "Verifying your access...",
  showSkeleton = true,
  timeoutSeconds = 8
}) => {
  const [loadTimeout, setLoadTimeout] = useState(false);
  const { forceRefreshAuth } = useSupabaseAuth();

  useEffect(() => {
    const timer = setTimeout(() => {
      console.warn(`⏱️ Auth loading timeout reached (${timeoutSeconds}s)`);
      setLoadTimeout(true);
    }, timeoutSeconds * 1000);

    return () => clearTimeout(timer);
  }, [timeoutSeconds]);

  if (loadTimeout) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <div className="max-w-md w-full space-y-6 text-center">
          <div className="space-y-3">
            <div className="text-5xl">⚠️</div>
            <h2 className="text-2xl font-bold">Having Trouble Verifying Access</h2>
            <p className="text-muted-foreground">
              Authentication is taking longer than expected. This might be due to a slow connection or session issue.
            </p>
          </div>
          
          <div className="space-y-3">
            <Button 
              onClick={async () => {
                setLoadTimeout(false);
                await forceRefreshAuth();
              }}
              variant="default"
              className="w-full"
              size="lg"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry Authentication
            </Button>
            <Button 
              onClick={() => {
                sessionStorage.clear();
                window.location.href = '/login';
              }}
              variant="outline"
              className="w-full"
            >
              Return to Login
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground">
            If this issue persists, try clearing your browser cache or using a different browser.
          </p>
        </div>
      </div>
    );
  }

  if (showSkeleton) {
    return <AppShellSkeleton />;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center space-y-4">
        <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
        <h2 className="text-lg font-medium">{message}</h2>
        <p className="text-sm text-muted-foreground">This should only take a moment...</p>
      </div>
    </div>
  );
};

export default AuthLoadingScreen;
